import { ApiService, UserType } from '@shared/services/api.service';
import { WorkplaceEventRouter } from '../events/WorkplaceEventRouter';
import { authService } from '@host/services/auth.service';
import { UserStatus, UserStatusChangedEvent, WorkplaceBootstrapDto } from 'nexgen-types';
import { useMeetingStore, useNotificationStore, useWorkplaceStore } from '@host/stores';
import { useUserProfileStore } from '@host/stores/user-profile.store';
import { useFloorManagerStore } from '@apps/office/stores/FloorManagerStore';
import { useSessionStore } from '@host/stores/session.store';
import { OfficeService } from '@/apps/office/services/OfficeService';
import { GlobalEventService } from '@/host/services/event.service';
import { WorkplaceBootstrapDtoSchema } from 'nexgen-types';
import { DEFAULT_PROFILE_PICTURE } from '@/utils/constants';
import { useMyFloorStore } from '@/apps/office/stores/myFloorStore';
import { useMultiFloorStateStore } from '@/apps/office/stores/MultiFloorStore';
import { useAdministrationStore } from '@host/stores/administration.store';
import { Logger } from '../../../shared/services/logger.service';
import { BreakService } from './BreakService';
import { RoomService } from '@/shared/services/roomServices';
import { takeReloadRoomForRestore } from '@host/utils/reload-room-snapshot';

// ---- API Endpoints ----
const API_ENDPOINTS = {
  BOOTSTRAP: {
    endpoint: 'workplace/bootstrap',
    service: 'app' as const,
  },
  USER_ORG_PREFERENCES: {
    endpoint: 'user-org-preferences/app-settings/office',
    service: 'app' as const,
  },
  USER_PRESENCE: {
    endpoint: 'workplace/user/presence',
    service: 'app' as const,
  },
  ROOM_OCCUPANTS: {
    endpoint: 'workplace/rooms/occupants',
    service: 'app' as const,
  },
} as const;

// ---- Constants ----
export const DEFAULT_BG =
  'https://nexgen-dev-org-files-public.s3.us-east-1.amazonaws.com/app/defaults/office-bg.jpg';
export const DEFAULT_AVATAR = DEFAULT_PROFILE_PICTURE;

export class WorkplaceService {
  static getCurrentState(): any {
    return useWorkplaceStore.getState();
  }

  /**
   * Initialize the workplace with bootstrap data.
   * This method handles both fresh logins and regular initializations.
   *
   * @param isFreshLogin - Whether this is a fresh login (default: false)
   * @param isGuest - Whether this is a guest user (default: false)
   * @returns WorkplaceBootstrapDto - The initialized workplace data
   */
  static async initializeWorkplace(isFreshLogin = false): Promise<WorkplaceBootstrapDto> {
    const workplaceStore = useWorkplaceStore.getState();
    const floorManagerStore = useFloorManagerStore.getState();
    const sessionStore = useSessionStore.getState();
    const meetingStore = useMeetingStore.getState();

    // Honor session-replaced reload signal (set by MeetingEventRouter before reload)
    const forceHomeRoom = sessionStorage.getItem('force_home_room_on_init') === '1';
    if (forceHomeRoom) {
      sessionStorage.removeItem('force_home_room_on_init');
      isFreshLogin = true;
      workplaceStore.setFreshLogin(true);
    }

    workplaceStore.setInitLoading(true);
    workplaceStore.setInitError(null);

    try {
      WorkplaceEventRouter.registerEventListeners();

      // Initialize auth service first (only on fresh login)
      if (isFreshLogin) {
        authService.initialize();
      }

      const endpoint = API_ENDPOINTS.BOOTSTRAP;
      const userType = UserType.PlatformUser;

      const raw = await ApiService.get(endpoint.endpoint, {
        service: endpoint.service,
        userType: userType,
      });

      // Use appropriate schema for regular users
      const validated = WorkplaceBootstrapDtoSchema.parse(raw);
      Logger.debug('🚀 ~ WorkplaceService ~ validated:', validated.presence.background_url);

      // Capture fresh login state BEFORE updating stores
      const wasFreeshLogin = isFreshLogin;

      // Update stores (this will reset isFreshLogin to false)
      sessionStore.setOrgPid(validated.org.pid);

      const userProfileStore = useUserProfileStore.getState();
      userProfileStore.setProfile(validated.profile);

      // Initialize workplace store for regular users
      workplaceStore.initFromBootstrap(validated);

      // Fetch org settings in background (non-blocking)
      workplaceStore.fetchOrgSettings().catch((error: any) => {
        console.warn('[WorkplaceService] Failed to fetch org settings:', error);
      });

      // Restore break state if user has an active break (handles page refresh / network recovery).
      // Awaited so we know break status before joining meetings below.
      let isOnBreak = false;
      try {
        const breakDetails = await BreakService.getInstance().getBreakDetails();
        isOnBreak = !!breakDetails;
      } catch (error: any) {
        console.warn('[WorkplaceService] ⚠️ Failed to restore break state:', error);
      }

      // If no active break, sync the room's ooo_message with the user's persistent
      // office message from settings. This clears any stale break message left behind
      // by a previous session that ended while on break, and restores a plain office
      // message if the user had one configured.
      if (!isOnBreak) {
        RoomService.getAppSettings()
          .then((appSettings) => {
            const persistentOfficeMessage = appSettings?.settings?.['office-message'] ?? null;
            return RoomService.updateOfficeData(persistentOfficeMessage);
          })
          .catch((error: any) => {
            console.warn('[WorkplaceService] ⚠️ Failed to sync office message on login:', error);
          });
      }

      // Fetch policies for the organization (needed for policy validation)
      const administrationStore = useAdministrationStore.getState();
      administrationStore.fetchPolicies().catch((error: any) => {
        console.warn('[WorkplaceService] ⚠️ Failed to fetch policies:', error);
      });

      // Determine the actual room the user will be placed in after bootstrap
      const shouldUseHomeRoom =
        wasFreeshLogin && !!validated.profile && !!validated.profile.home_room_pid;

      let actualRoomPid = shouldUseHomeRoom
        ? validated.profile.home_room_pid
        : validated.presence.room_pid;
      let actualFloorPid = shouldUseHomeRoom
        ? validated.profile.home_floor_pid || validated.presence.floor_pid
        : validated.presence.floor_pid;

      // NGE-1305: page reloaded from a non-home room that the server no longer reports as
      // the user's presence (e.g. its offline write landed after the beforeunload presence
      // PATCH). Restore that room through the full navigation flow instead of joining the
      // server's room — otherwise the meeting/video/background showed the home room while
      // the avatar was restored in the original room. Taken on every init (even when not
      // used) so a later re-init in the same page never restores a stale snapshot.
      const reloadSnapshot = takeReloadRoomForRestore();
      const reloadRestore =
        !wasFreeshLogin &&
        reloadSnapshot &&
        reloadSnapshot.floorPid &&
        reloadSnapshot.roomPid !== validated.profile.home_room_pid &&
        reloadSnapshot.roomPid !== validated.presence.room_pid
          ? { roomPid: reloadSnapshot.roomPid, floorPid: reloadSnapshot.floorPid }
          : null;
      const isReloadRoomAvailable = reloadRestore
        ? await this.isReloadRoomAvailable(reloadRestore.floorPid, reloadRestore.roomPid)
        : false;

      if (reloadRestore && isReloadRoomAvailable) {
        actualRoomPid = reloadRestore.roomPid;
        actualFloorPid = reloadRestore.floorPid;
      } else {
        if (reloadRestore) {
          useNotificationStore
            .getState()
            .showSnackbar(
              'The room you were in is no longer available. You have been moved to your home office.',
              'info'
            );
        }

        // Skip joining the meeting on restore if the user is currently on break.
        // A restored reload room joins its meeting through navigateToRoom below instead,
        // after the lock check — joining here first would skip that check.
        meetingStore.setCurrentRoomId(
          actualRoomPid || validated.presence.room_pid,
          isOnBreak ? { skipMeetingJoin: true } : undefined
        );
      }

      if (actualFloorPid) {
        floorManagerStore.loadMyFloor(actualFloorPid);

        // A pending refresh restore owns the viewed floor; don't flash the saved server floor.
        if (!useMyFloorStore.getState().isRestoringFloor) {
          useMyFloorStore.getState().setCurrentFloorId(actualFloorPid);
        }
      }

      // Service handles business logic and events
      Logger.debug('[WorkplaceService] Fresh login detected - handling auth flow');

      // Update status first

      if (reloadRestore && isReloadRoomAvailable) {
        const restored = await this.navigateToReloadRoom(
          reloadRestore.roomPid,
          reloadRestore.floorPid,
          validated.profile.pid,
          isOnBreak
        );

        if (!restored) {
          // Fall back to the room the server placed the user in (their home office)
          actualRoomPid = validated.presence.room_pid;
          actualFloorPid = validated.presence.floor_pid;
          await this.fallbackFromReloadRoom(validated, isOnBreak);
        }
      } else if (actualRoomPid && actualFloorPid) {
        // If user was placed in home room (different from presence room), trigger proper room navigation
        Logger.debug(
          '[WorkplaceService] User placed in home room on fresh login - triggering room navigation'
        );
        try {
          // Trigger proper room navigation to ensure all visual updates and events are handled
          await this.updateUserPresence(
            actualFloorPid,
            actualRoomPid,
            validated.profile.pid,
            'active'
          );
          await OfficeService.getInstance().navigateToRoom({
            roomPid: actualRoomPid,
            floorPid: actualFloorPid,
            userPid: validated.profile.pid,
            room: {
              pid: actualRoomPid,
              name: validated.presence.room_name || 'Home Room',
              floor_pid: actualFloorPid,
              background_url: validated.presence?.background_url || '',
              occupants: [],
              displayName: validated.presence.room_name || 'Home Room',
            },
            floor: null, // Will be loaded by the navigation service
            skipMeetingJoin: isOnBreak,
          });

          Logger.debug('[WorkplaceService] ✅ Room navigation completed for home room');
        } catch (error) {
          console.warn('[WorkplaceService] ⚠️ Failed to navigate to home room:', error);
        }
      } else {
        await this.updateUserStatus('active');
      }

      Logger.debug('[WorkplaceService] ✅ Fresh login auth flow completed');

      // Restore locked status if the user was locked before reloading.
      // Two sources:
      // 1. sessionStorage CrtStatus — set by beforeunload for non-home rooms (conference rooms)
      // 2. validated.presence.status — the server-side status; used for home rooms because
      //    beforeunload returns early for home rooms and never writes CrtStatus
      const savedStatus = sessionStorage.getItem('CrtStatus');
      const wasLockedOnReload =
        !wasFreeshLogin && !isOnBreak && validated.presence.status === 'locked';

      if (savedStatus === 'locked' || wasLockedOnReload) {
        sessionStorage.removeItem('CrtStatus');
        await useWorkplaceStore.getState().setRoomLocked(true);
        Logger.debug('[WorkplaceService] 🔒 Restored locked status after reload');
      }

      // Wait for stores to fully update
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Service handles event emission - use actual room/floor where user is placed
      const loginPayload = {
        user_pid: validated.profile.pid,
        room_pid: actualRoomPid || validated.presence.room_pid,
        floor_pid: actualFloorPid || validated.presence.floor_pid,
        org_pid: validated.org.pid,
      };

      await authService.sendLoginEvent(loginPayload);

      // Bootstrap can still return offline after a tab close; login updates the DB to active
      // but own status events are skipped locally — sync UI so the avatar ring matches the DB.
      if (!wasFreeshLogin && !isOnBreak && savedStatus !== 'locked' && !wasLockedOnReload) {
        const activeStatus: UserStatus = 'active';
        const wp = useWorkplaceStore.getState();
        if (wp.user?.status === 'offline') {
          wp.setUserStatus(activeStatus);
          const floorPid = actualFloorPid || validated.presence.floor_pid;
          if (floorPid) {
            useMultiFloorStateStore
              .getState()
              .updateUserStatus(floorPid, validated.profile.pid, activeStatus);
          }
        }
      }

      return validated;
    } catch (err: any) {
      console.error('[WorkplaceService] ❌ Initialization failed:', err);
      workplaceStore.setInitError(err);
      throw err;
    } finally {
      workplaceStore.setInitLoading(false);
    }
  }

  // Handle user logout with proper auth cleanup
  static async handleLogout(): Promise<void> {
    try {
      const userProfileStore = useUserProfileStore.getState();
      const workplaceStore = useWorkplaceStore.getState();

      if (userProfileStore.profile?.pid) {
        Logger.debug('[WorkplaceService] Handling logout with auth service');

        // workplaceStore.presence lags behind live in-app navigation (it's only
        // synced from the server, not updated by OfficeService.navigateToRoom),
        // so it can report a stale floor/room here. currentFloorPresence is set
        // synchronously the moment a live room change happens and is the
        // reliable source for "where the user actually is right now".
        const logoutPayload = {
          user_pid: userProfileStore.profile.pid,
          floor_pid:
            useMultiFloorStateStore.getState().currentFloorPresence ||
            workplaceStore.currentRoom?.floor_pid ||
            workplaceStore.presence?.floor_pid,
          room_pid: workplaceStore.currentRoom?.pid || workplaceStore.presence?.room_pid,
        };

        // Use auth service for logout
        await authService.handleLogout(logoutPayload);
      }

      Logger.debug('[WorkplaceService] ✅ Logout completed');
    } catch (error) {
      console.error('[WorkplaceService] ❌ Error during logout:', error);
    }
  }

  // Clean updateUserStatus method - static and using proper BaseEvent
  static async updateUserStatus(status: UserStatus): Promise<void> {
    try {
      Logger.debug('[WorkplaceService] Updating user status to:', status);

      // Get current user context
      const userProfileStore = useUserProfileStore.getState();
      const workplaceStore = useWorkplaceStore.getState();

      const userPid = userProfileStore.profile?.pid;
      const floorPid = workplaceStore.presence?.floor_pid;
      const roomPid = workplaceStore.presence?.room_pid;
      const orgPid = workplaceStore.org?.pid;

      if (!userPid) {
        throw new Error('User PID not available');
      }

      const statusEvent = new UserStatusChangedEvent({
        user_pid: userPid,
        floor_pid: floorPid,
        org_pid: orgPid,
        room_pid: roomPid,
        status: status,
      });
      Logger.debug('[WorkplaceService] Created status event instance:', statusEvent);
      await this.persistUserStatus(userPid, status);
      await GlobalEventService.emitTyped(statusEvent);
      Logger.debug('[WorkplaceService] ✅ Status event sent successfully');
    } catch (error) {
      console.error('[WorkplaceService] ❌ Status event failed:', error);
      // Don't throw - status updates shouldn't break the app
    }
  }

  // Keep all your existing methods:
  static async persistUserStatus(userId: string, status: string, message?: string): Promise<void> {
    const config = API_ENDPOINTS.USER_PRESENCE;

    const payload: { userId: string; status: string; message?: string } = {
      userId,
      status,
    };

    if (message) {
      payload.message = message;
    }

    await ApiService.patch(config.endpoint, payload, { service: config.service });
  }

  static async getRoomOccupants(roomId: string): Promise<any> {
    const config = API_ENDPOINTS.ROOM_OCCUPANTS;
    return ApiService.get(`${config.endpoint}/${roomId}`, { service: config.service });
  }

  /**
   * NGE-1305: whether the room the page was reloaded from still exists and its floor is
   * still accessible to the user. Also loads that floor's place/presence, which the lock
   * check in navigateToRoom reads.
   */
  private static async isReloadRoomAvailable(floorPid: string, roomPid: string): Promise<boolean> {
    try {
      const multiFloorStore = useMultiFloorStateStore.getState();
      await multiFloorStore.loadInitialState(floorPid);
      const rooms = useMultiFloorStateStore.getState().getFloorState(floorPid)?.place?.rooms;
      return !!rooms?.some((room: { pid: string }) => room.pid === roomPid);
    } catch (error) {
      console.warn('[WorkplaceService] ⚠️ Reload room is not available:', error);
      return false;
    }
  }

  /**
   * NGE-1305: places the user back in the room the page was reloaded from. Returns false
   * when that is not possible — e.g. the room was locked by its owner meanwhile, in which
   * case navigateToRoom sends the normal access request — so the caller can fall back.
   */
  private static async navigateToReloadRoom(
    roomPid: string,
    floorPid: string,
    userPid: string,
    isOnBreak: boolean
  ): Promise<boolean> {
    let error: string | undefined;
    try {
      const result = await OfficeService.getInstance().navigateToRoom({
        roomPid,
        floorPid,
        userPid,
        floor: null,
        // A user who was locked inside this room before the reload is part of that lock,
        // not a visitor asking to enter it.
        bypassLockCheck: sessionStorage.getItem('CrtStatus') === 'locked',
        skipMeetingJoin: isOnBreak,
      });

      if (result.success) {
        await this.updateUserPresence(floorPid, roomPid, userPid, 'active');
        Logger.debug('[WorkplaceService] ✅ Restored room after reload:', roomPid);
        return true;
      }
      error = result.error;
    } catch (navigationError: any) {
      console.warn('[WorkplaceService] ⚠️ Failed to restore room after reload:', navigationError);
    }

    useNotificationStore
      .getState()
      .showSnackbar(
        `Could not return you to the room you were in${error ? `: ${error}` : '.'} You have been moved to your home office.`,
        'info'
      );
    return false;
  }

  /** NGE-1305: puts the user in the room the server placed them in when a reload restore fails. */
  private static async fallbackFromReloadRoom(
    validated: WorkplaceBootstrapDto,
    isOnBreak: boolean
  ): Promise<void> {
    const { room_pid: roomPid, floor_pid: floorPid } = validated.presence;
    if (!roomPid || !floorPid) return;

    try {
      if (useWorkplaceStore.getState().currentRoom?.pid !== roomPid) {
        // navigateToRoom failed part-way through — run the full navigation back
        await OfficeService.getInstance().navigateToRoom({
          roomPid,
          floorPid,
          userPid: validated.profile.pid,
          room: {
            pid: roomPid,
            name: validated.presence.room_name || 'Home Room',
            floor_pid: floorPid,
            background_url: validated.presence?.background_url || '',
            occupants: [],
            displayName: validated.presence.room_name || 'Home Room',
          },
          floor: null,
          bypassLockCheck: true,
          skipMeetingJoin: isOnBreak,
        });
      } else {
        useMeetingStore
          .getState()
          .setCurrentRoomId(roomPid, isOnBreak ? { skipMeetingJoin: true } : undefined);
        useFloorManagerStore.getState().loadMyFloor(floorPid);
        useMyFloorStore.getState().setCurrentFloorId(floorPid);
      }

      await this.updateUserPresence(floorPid, roomPid, validated.profile.pid, 'active');
    } catch (error) {
      console.warn('[WorkplaceService] ⚠️ Failed to fall back from reload room:', error);
    }
  }

  static async updateUserPresence(
    floorId: string,
    roomId: string,
    userId: string,
    status?: UserStatus
  ): Promise<void> {
    const config = API_ENDPOINTS.USER_PRESENCE;
    await ApiService.patch(
      config.endpoint,
      {
        floorId,
        roomId,
        userId,
        ...(status && { status }),
      },
      { service: config.service }
    );
  }
}
